import { useMemo } from "react";

import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import type { AddressFormValues } from "./Address.types";

export const MIN_SUPPORTED_PHONE_NUMBER = 2;
export const MAX_SUPPORTED_PHONE_NUMBER = 16;

// mirrors backend validation in address.validation.ts
export const VALID_US_STATE_CODES = new Set<string>([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  // us territories
  "AS",
  "GU",
  "MP",
  "PR",
  "VI",
]);
export type SupportedLabelCountry = "US" | "CA" | "GB" | "IE" | "AU" | "NZ" | "OTHER";

export type CountryLabels = {
  postalCodeLabel: string;
  cityLabel: string;
  regionLabel: string;
};

export const useAddressCountryLabels = () => {
  const { t } = useTranslation(["address"]);

  return useMemo<Record<SupportedLabelCountry, CountryLabels>>(() => {
    return {
      US: {
        postalCodeLabel: t("address:zip_code"),
        regionLabel: t("address:state"),
        cityLabel: t("address:city"),
      },
      CA: {
        postalCodeLabel: t("address:postal_code"),
        regionLabel: t("address:province_territory"),
        cityLabel: t("address:city"),
      },
      GB: {
        postalCodeLabel: t("address:postcode"),
        regionLabel: t("address:county"),
        cityLabel: t("address:town_city"),
      },
      IE: {
        postalCodeLabel: t("address:eircode"),
        regionLabel: t("address:region"),
        cityLabel: t("address:town_city"),
      },
      AU: {
        postalCodeLabel: t("address:postcode"),
        regionLabel: t("address:state_territory"),
        cityLabel: t("address:suburb"),
      },
      NZ: {
        postalCodeLabel: t("address:postcode"),
        regionLabel: t("address:region"),
        cityLabel: t("address:suburb"),
      },
      OTHER: {
        postalCodeLabel: t("address:postal_code"),
        regionLabel: t("address:region"),
        cityLabel: t("address:city"),
      },
    };
  }, [t]);
};

const isValidUSState = (value: string | null | undefined) =>
  !value || VALID_US_STATE_CODES.has(value.toUpperCase());

/** what a saved address looks like coming back from the api, loose enough for any generated fragment */
type StoredAddress<P extends string> = {
  [K in keyof Omit<AddressFormValues, "countryCode" | "purpose">]?: string | null;
} & { countryCode?: string | null; purpose?: P | null };

/**
 * a saved address, or nothing, becomes form values
 *
 * the purpose fallback is passed in because each app has its own generated enum for it
 */
export const toAddressFormValues = <P extends string>(
  address: StoredAddress<P> | null | undefined,
  fallbackPurpose: P,
): Omit<AddressFormValues, "purpose"> & { purpose: P } => ({
  label: address?.label ?? null,
  recipientName: address?.recipientName ?? null,
  line1: address?.line1 ?? null,
  line2: address?.line2 ?? null,
  phone: address?.phone ?? null,
  city: address?.city ?? null,
  region: address?.region ?? null,
  postalCode: address?.postalCode ?? null,
  countryCode: (address?.countryCode ?? "US") as AddressFormValues["countryCode"],
  purpose: address?.purpose ?? fallbackPurpose,
});

/** form values become what the create and update address mutations take, an empty phone has to be null because the db check constraint rejects an empty string */
export const toAddressInput = <P extends string>(
  address: Omit<AddressFormValues, "purpose"> & { purpose: P },
) => ({
  label: address.label,
  recipientName: address.recipientName,
  line1: address.line1,
  line2: address.line2,
  phone: address.phone?.trim() ? address.phone : null,
  city: address.city,
  // a cleared input is "", which the api reads as a supplied but invalid state rather than as none
  region: address.region?.trim() ? address.region : null,
  postalCode: address.postalCode,
  countryCode: address.countryCode,
  purpose: address.purpose,
});

/** country and state rules for a physical location, kept here so the four forms that collect one cannot drift apart */
/**
 * the region field on its own
 *
 * a supplied value is always checked for shape and, in the US, for being a real state, only whether one has to be supplied varies
 */
export const regionSchema = (t: TFunction, stateRequired: boolean) => {
  const base = yup
    .string()
    .nullable()
    // a cleared input arrives as "", treat it as absent so the length and state checks only run on a real value
    .transform((value: string | null) => (value === "" ? null : value))
    .length(2, t("yup:two_letter_code"))
    .when("countryCode", {
      is: "US",
      then: (schema) => schema.test("valid-us-state", t("yup:invalid_us_state"), isValidUSState),
    });

  if (stateRequired) {
    return base.required(t("yup:state_province_required"));
  }

  return base;
};

/**
 * country and state rules for a physical location, kept here so the forms that collect one cannot drift apart
 *
 * only a system has to carry a state, its region decides which regulatory templates apply, nothing reads an org or site region
 */
export const locationAddressSchema = (t: TFunction, { stateRequired }: { stateRequired: boolean }) =>
  yup.object({
    countryCode: yup.string().required(t("yup:country_required")),
    region: regionSchema(t, stateRequired),
  });
